import { List, OrderedMap, Set } from 'immutable';

type Id = string;

interface HasId {
  id: string;
}

type Children<T> = {
  folders: OrderedMap<Id, Folder<T>>;
  leaves: OrderedMap<Id, Leaf<T>>;
};

type Folder<T> = {
  id: string;
  children: Children<T>;
  parent?: Folder<T>;
};

type Leaf<T> = {
  value: T;
  parent?: Folder<T>;
};

type FolderTrieFields<T> = {
  root: Children<T>;
  allFolders: OrderedMap<Id, Folder<T>>;
  allLeaves: OrderedMap<Id, Leaf<T>>;
};

/**
 * Trie that stores items in folders. FolderIds overlap with LeafIds.
 * Provides function chaining but is not immutable, more work needed to make fully immutable.
 */
class FolderTrie<T extends HasId> {
  private root: Children<T> = {
    folders: OrderedMap<Id, Folder<T>>(),
    leaves: OrderedMap<Id, Leaf<T>>(),
  };

  private allFolders = OrderedMap<Id, Folder<T>>();

  private allLeaves = OrderedMap<Id, Leaf<T>>();

  constructor(__fields__?: FolderTrieFields<T>) {
    if (__fields__) {
      const { root, allFolders, allLeaves } = __fields__;

      this.root = {
        ...root,
      };
      this.allFolders = allFolders;
      this.allLeaves = allLeaves;
    }
  }

  private clone(): FolderTrieFields<T> {
    const { root, allLeaves, allFolders } = this;

    return {
      root,
      allLeaves,
      allFolders,
    };
  }

  private getPathToLeaf(leafId: string): string[] | undefined {
    const leaf = this.allLeaves.get(leafId);

    if (!leaf) {
      return undefined;
    }

    if (!leaf.parent) {
      return [];
    }

    let currentFolder: Folder<T> | undefined = leaf.parent;
    let reversedFolderPathIds = List<string>([currentFolder.id]);

    while (currentFolder) {
      currentFolder = currentFolder.parent;

      if (currentFolder) {
        reversedFolderPathIds = reversedFolderPathIds.push(currentFolder.id);
      }
    }

    return reversedFolderPathIds.reverse().toArray();
  }

  private removeFolder(folderId: string): FolderTrie<T> {
    const newTrie = this.clone();
    const folder = newTrie.allFolders.get(folderId);

    if (!folder) {
      return new FolderTrie(newTrie);
    }

    const descendantLeafIds = this.descendantLeaves(folderId).map((l) => l.id);

    newTrie.allLeaves = newTrie.allLeaves.removeAll(descendantLeafIds);
    newTrie.allFolders = newTrie.allFolders.remove(folderId);

    if (folder.parent) {
      folder.parent.children.folders =
        folder.parent.children.folders.remove(folderId);
    } else {
      newTrie.root.folders = newTrie.root.folders.remove(folderId);
    }

    return new FolderTrie(newTrie);
  }

  insert(folderIds: string[], value: T): FolderTrie<T> {
    const isPartOfPathALeaf = folderIds.some((folderId) =>
      this.allLeaves.has(folderId)
    );

    if (isPartOfPathALeaf) {
      return new FolderTrie(this.clone());
    }

    const newTrie = this.removeFolder(value.id).delete(value.id);

    type ReducerNode =
      | {
          type: 'root';
          nodeValue: Children<T>;
        }
      | {
          type: 'folder';
          nodeValue: Folder<T>;
        };

    const finalNode = folderIds.reduce(
      (currentNode, folderId) => {
        const { type, nodeValue } = currentNode;

        if (type === 'root') {
          const { folders } = nodeValue as Children<T>;

          if (!folders.get(folderId)) {
            const newFolder = {
              id: folderId,
              children: {
                folders: OrderedMap<Id, Folder<T>>(),
                leaves: OrderedMap<Id, Leaf<T>>(),
              },
            };

            (nodeValue as Children<T>).folders = folders.set(
              folderId,
              newFolder
            );
            newTrie.allFolders = newTrie.allFolders.set(folderId, newFolder);
          }

          return {
            type: 'folder',
            nodeValue: newTrie.allFolders.get(folderId),
          } as ReducerNode;
        }

        const folder = nodeValue as Folder<T>;

        const { folders } = folder.children;

        if (!folders.get(folderId)) {
          const newFolder = {
            id: folderId,
            children: {
              folders: OrderedMap<Id, Folder<T>>(),
              leaves: OrderedMap<Id, Leaf<T>>(),
            },
            parent: folder,
          };
          folder.children.folders = folders.set(folderId, newFolder);
          newTrie.allFolders = newTrie.allFolders.set(folderId, newFolder);
        }

        return {
          type: 'folder',
          nodeValue: newTrie.allFolders.get(folderId),
        } as ReducerNode;
      },
      { type: 'root', nodeValue: newTrie.root } as ReducerNode
    );

    if (finalNode.type === 'root') {
      const { leaves } = finalNode.nodeValue;
      const newLeaf = { value };
      const { id } = value;

      finalNode.nodeValue.leaves = leaves.set(id, newLeaf);
      newTrie.allLeaves = newTrie.allLeaves.set(id, newLeaf);
    }

    if (finalNode.type === 'folder') {
      const folder = finalNode.nodeValue;
      const { leaves } = folder.children;
      const { id } = value;
      const newLeaf = {
        value,
        parent: folder,
      };

      folder.children.leaves = leaves.set(id, newLeaf);
      newTrie.allLeaves = newTrie.allLeaves.set(id, newLeaf);
    }

    return newTrie;
  }

  insertAll(leaves: { folderPathIds: string[]; value: T }[]): FolderTrie<T> {
    return leaves.reduce((trie, { folderPathIds, value }) => {
      return trie.insert(folderPathIds, value);
    }, new FolderTrie(this.clone()));
  }

  delete(leafId: string): FolderTrie<T> {
    const newTrie = this.clone();
    const leaf = newTrie.allLeaves.get(leafId);

    if (!leaf) {
      return new FolderTrie(newTrie);
    }

    newTrie.allLeaves = newTrie.allLeaves.delete(leafId);

    let { parent } = leaf;

    if (parent) {
      parent.children.leaves = parent.children.leaves.delete(leafId);
    } else {
      newTrie.root.leaves = newTrie.root.leaves.delete(leafId);
    }

    while (parent && this.isEmpty(parent.id)) {
      const grandParent = parent.parent;

      if (grandParent) {
        grandParent.children.folders = grandParent.children.folders.delete(
          parent.id
        );
      } else {
        newTrie.root.folders = newTrie.root.folders.delete(parent.id);
      }

      newTrie.allFolders = newTrie.allFolders.delete(parent.id);

      parent = grandParent;
    }

    return new FolderTrie(newTrie);
  }

  deleteAll(leafIds: string[]): FolderTrie<T> {
    return leafIds.reduce((trie, leafId) => {
      return trie.delete(leafId);
    }, new FolderTrie(this.clone()));
  }

  isEmpty(folderId?: string): boolean {
    let folder;

    if (folderId) {
      folder = this.allFolders.get(folderId);

      if (!folder) {
        return true;
      }
    }

    const { leaves, folders } = folder ? folder.children : this.root;

    return leaves.count() + folders.count() === 0;
  }

  private descendantLeavesHelper(folders: IterableIterator<Folder<T>>): T[] {
    let folder = folders.next();
    let leaves = [] as T[];

    while (!folder.done) {
      const { children } = folder.value;
      const immediateLeafIds = children.leaves.toArray().map((l) => l[1].value);

      leaves = leaves.concat([
        ...immediateLeafIds,
        ...this.descendantLeavesHelper(children.folders.values()),
      ]);

      folder = folders.next();
    }

    return leaves;
  }

  descendantLeaves(folderId?: string): T[] {
    if (!folderId) {
      return this.allLeaves.toArray().map((l) => l[1].value);
    }

    const folder = this.allFolders.get(folderId);

    if (!folder) {
      return [];
    }

    const { leaves, folders } = folder.children;
    const leafIds = leaves.toArray().map((l) => l[1].value);

    return leafIds.concat(this.descendantLeavesHelper(folders.values()));
  }

  has(leafId: string, folderIds?: string[]): boolean {
    const hasLeafInTrie = Boolean(this.allLeaves.get(leafId));

    if (!folderIds) {
      return hasLeafInTrie;
    }

    const pathToLeaf = this.getPathToLeaf(leafId);
    if (!pathToLeaf) {
      return false;
    }

    if (folderIds.length !== pathToLeaf.length) {
      return false;
    }

    const matchesFolderPath = pathToLeaf.every(
      (trieFolderId, index) => trieFolderId === folderIds[index]
    );
    return matchesFolderPath;
  }

  private folderToString(folder: Folder<T>, depth: number): string {
    const { leaves, folders } = folder.children;

    const indent = '    '.repeat(depth);

    return `\n${indent}${folder.id}\n${indent}Leaves:${
      leaves.count()
        ? leaves.toArray().map((f) => ` ${f[1].value.id}`)
        : ' None'
    }\n${indent}Folders: ${
      folders.count()
        ? folders
            .toArray()
            .map((f) => `${this.folderToString(f[1], depth + 1)}`)
        : 'None'
    }\n`;
  }

  validateFolder({
    folderId,
    childIds,
  }: {
    folderId?: string;
    childIds: string[];
  }): boolean {
    const folderChildren = (() => {
      if (!folderId) return this.root;
      const folder = this.allFolders.get(folderId);
      return folder ? folder.children : undefined;
    })();

    const folderLeaves: Set<Id> = !folderChildren
      ? Set()
      : Set(folderChildren.leaves.toArray().map((leaf) => leaf[1].value.id));

    const hasChildExpectedElseWhere = childIds.some(
      (childId) => !folderLeaves.has(childId) && this.allLeaves.get(childId)
    );

    const childIdsSet = Set(childIds);
    const hasExpectedChildMissingHere = folderLeaves.some(
      (leafId) => !childIdsSet.get(leafId)
    );

    return !(hasChildExpectedElseWhere || hasExpectedChildMissingHere);
  }

  toString(): string {
    const { leaves, folders } = this.root;

    const rootString = (() => {
      return `Leaves:${
        leaves.count()
          ? leaves.toArray().map((f) => ` ${f[1].value.id}`)
          : ' None'
      }\nFolders: ${
        folders.count()
          ? folders.toArray().map((f) => `${this.folderToString(f[1], 1)}`)
          : 'None'
      }`;
    })();

    return `Root\n${rootString}\nAll Leaves:${this.allLeaves
      .toArray()
      .map((f) => ` ${f[1].value.id}`)}\nAll Folders:${this.allFolders
      .toArray()
      .map((f) => ` ${f[1].id}`)}`;
  }
}

export default FolderTrie;
